<template>
    <v-container fluid>
        <!-- Tab Navigation -->
        <v-tabs v-model="selectedTabIndex" @change="updateTab">
            <v-tab v-for="tab in filteredTabs" :key="tab.value">{{ tab.text }}</v-tab>
        </v-tabs>

        <v-toolbar flat>
            <v-toolbar-title><b>Subtitle Ingest Dashboard</b></v-toolbar-title>
            <v-spacer></v-spacer>

            <!-- 현재 시간대 표시 -->
            <v-chip color="primary" outlined>Timezone: {{ timezone }}</v-chip>

            <!-- Export 버튼 -->
            <v-btn @click="exportToExcel" icon color="darkgrey">
                <v-icon>mdi-file-download</v-icon>
            </v-btn>

            <!-- 필터 선택 -->
            <v-select
                class="filter-select"
                v-model="selectedFilter"
                :items="filterOptions"
                label="Search by"
                solo
                style="max-width: 150px; margin-bottom: 0px;"
            ></v-select>

            <!-- 일반 검색 (텍스트 필터) -->
            <template v-if="selectedFilter === 'episode_title'">
                <v-text-field
                    class="search-field"
                    v-model="search"
                    label="Search"
                    append-icon="mdi-magnify"
                    single-line
                    hide-details
                    style="max-width: 400px;"
                ></v-text-field>
            </template>

            <!-- CP와 Category 필터 -->
            <template v-if="selectedFilter === 'cp' || selectedFilter === 'category'">
                <v-select
                    class="search-field"
                    v-model="search"
                    :items="dropdownOptions"
                    label="Select"
                    solo
                    hide-details
                ></v-select>
            </template>

            <!-- 날짜 필터 -->
            <template v-if="selectedFilter === 'on_air_date' || selectedFilter.includes('ingested_date') || selectedFilter.includes('qc_date')">
                <v-menu
                    ref="dateMenu"
                    v-model="menu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                            v-model="dateRangeText"
                            label="Select Date Range"
                            append-icon="mdi-calendar"
                            readonly
                            v-bind="attrs"
                            v-on="on"
                        ></v-text-field>
                    </template>

                    <!-- Date Picker -->
                    <v-date-picker v-model="selectedDates" range scrollable @change="updateDateRange">
                        <v-spacer></v-spacer>
                        <v-btn text color="primary" @click="menu = false">OK</v-btn>
                    </v-date-picker>
                </v-menu>
            </template>
        </v-toolbar>
        <!-- Data Table -->
        <div style="width: 100%; overflow-x:auto;" class="scrollable-table">
            <v-data-table 
                :headers="filteredHeaders" 
                :items="filteredTitleData" 
                :items-per-page="itemsPerPage" 
                :page.sync="page"
                class="custom-table">
                <template v-slot:empty>
                    <v-alert>No data available</v-alert>
                </template>
            </v-data-table>
        </div>
    </v-container>
</template>

<script>
import axios from 'axios';
import { mapState } from 'vuex';
import * as XLSX from 'xlsx'; // xlsx 라이브러리 임포트

export default {
    data() {
        return {
            loading: true,
            page: 1,
            itemsPerPage: 10,
            data: [],
            search: '',
            selectedFilter: 'episode_title', // 기본 검색 필터
            selectedDates: [], // Date Picker에서 선택된 날짜 범위
            dateRangeText: '', // 선택된 날짜 범위 텍스트
            menu: false, // 달력 메뉴 상태
            dropdownOptions: [], // CP 또는 Category 필드에 대한 옵션 리스트
            selectedTabIndex: 0,
            selectedTab: 'Master', // Default tab is Master
            tabs: [
                { text: 'Portuguese', value: 'Portuguese' },
                { text: 'Spanish', value: 'Spanish' },
                { text: 'Vietnamese', value: 'Vietnamese' },
                { text: 'Chinese', value: 'Chinese' },
                { text: 'Old', value: 'Old' }
            ],
        };
    },
    watch: {
        selectedFilter(newFilter) {
            if (newFilter === 'cp' || newFilter === 'category') {
                this.setDropdownOptions();
            }
        },
        selectedTab() {
            this.updateFilteredData();
        },
    },
    computed: { 
        ...mapState(['auth']),
        totalPages() {
            return Math.ceil(this.filteredTitleData.length / this.itemsPerPage);
        },
        filteredTabs() {
            if (this.auth === 'PORTUGUESE') {
                return this.tabs.filter(tab => tab.value === 'Portuguese' || tab.value === 'Old');
            } else if (this.auth === 'SPANISH') {
                return this.tabs.filter(tab => tab.value === 'Spanish' || tab.value === 'Old');
            } else if (this.auth === 'VIETNAMESE') {
                return this.tabs.filter(tab => tab.value === 'Vietnamese');
            } else if (this.auth === 'CHINESE') {
                return this.tabs.filter(tab => tab.value === 'Chinese');
            }
            return this.tabs;
        },
        filterOptions() {
            const commonOptions = [
                { text: 'Title', value: 'episode_title' },
                { text: 'CP', value: 'cp' },
                { text: 'Category', value: 'category' },
                { text: 'On Air Date', value: 'on_air_date' },
                { text: 'English', value: 'english_ingested_date' }
            ];

            // auth에 따라 다르게 필터 옵션 구성
            if (this.auth === 'PORTUGUESE') {
                return [
                    ...commonOptions,
                    { text: 'Portuguese', value: 'portuguese_ingested_date' },
                    { text: 'Portuguese QC', value: 'portuguese_qc_date' }
                ];
            } else if (this.auth === 'SPANISH') {
                return [
                    ...commonOptions,
                    { text: 'Spanish', value: 'spanish_ingested_date' },
                    { text: 'Spanish QC', value: 'spanish_qc_date' }
                ];
            } else if (this.auth === 'VIETNAMESE') {
                return [
                    ...commonOptions,
                    { text: 'Vietnamese', value: 'vietnamese_ingested_date' }
                ];
            } else if (this.auth === 'CHINESE') {
                return [
                    ...commonOptions,
                    { text: 'Chinese', value: 'chinese_ingested_date' },
                    { text: 'Chinese QC', value: 'chinese_qc_date' }
                ];
            }
            return commonOptions; // 기본 공통 옵션만 포함
        },
	filteredHeaders() {
            const baseHeaders = [
                { text: 'CP', value: 'cp', width: '90px' }, 
                { text: 'TA', value: 'english_ta', width: '80px' }, 
                { text: 'Category', value: 'category' , width: '80px'},
                { text: 'Title', value: 'episode_title' },
                { text: 'DT (sec)', value: 'duration', width : '60px' },
                { text: 'DT (min)', value: 'durationMin', width: '80px' }, 
                { text: 'Min', value: 'min', width: '60px' },
                { text: 'On Air Date', value: 'on_air_date', width: '162px', align: 'center' },
                { text: 'English', value: 'english_ingested_date', width: '162px', align: 'center' },
            ];

            // Auth에 따른 동적 헤더 추가
            if (this.auth === 'PORTUGUESE') {
                const hasPortugueseData = this.titleData.some(
                    item => item.portuguese_ingested_date !== ''|| item.portuguese_qc_date!== ''
                );
                return [
                    ...baseHeaders,
                    ...(hasPortugueseData
                        ? [
                            { text: 'Portuguese', value: 'portuguese_ingested_date', width: '162px', align: 'center' },
                            { text: 'Portuguese QC', value: 'portuguese_qc_date', width: '162px', align: 'center' }
                        ]
                        : [])
                ];
            } else if (this.auth === 'SPANISH') {
                const hasSpanishData = this.titleData.some(
                    item => item.spanish_ingested_date !== ''|| item.spanish_qc_date !== ''
                );
                return [
                    ...baseHeaders,
                    ...(hasSpanishData
                        ? [
                            { text: 'Spanish', value: 'spanish_ingested_date', width: '162px', align: 'center' },
                            { text: 'Spanish QC', value: 'spanish_qc_date', width: '162px', align: 'center' }
                        ]
                        : [])
                ];
            } else if (this.auth === 'VIETNAMESE') {
                const hasVietnameseData = this.titleData.some(
                    item => item.vietnamese_ingested_date !== ''
                );
                return [
                    ...baseHeaders,
                    ...(hasVietnameseData
                        ? [
                            { text: 'Vietnamese', value: 'vietnamese_ingested_date', width: '162px', align: 'center' }
                        ]
                        : [])
                ];
            } else if (this.auth === 'CHINESE') {
                const hasChineseData = this.titleData.some(
                    item => item.chinese_ingested_date !== '' || item.chinese_qc_date !== ''
                );
                return [
                    ...baseHeaders,
                    ...(hasChineseData
                        ? [
                            { text: 'Chinese', value: 'chinese_ingested_date', width: '162px', align: 'center' },
                            { text: 'Chinese QC', value: 'chinese_qc_date', width: '162px', align: 'center' }
                        ]
                        : [])
                ];
            }
            return baseHeaders;
        },
        timezone() {
            // 각 auth별로 시간대 반환
            switch (this.auth) {
                case 'PORTUGUESE': return 'BRT (UTC−03:00)';
                case 'SPANISH': return 'CST (UTC−06:00)';
                case 'VIETNAMESE': return 'ICT (UTC+07:00)';
                case 'CHINESE': return 'CST (UTC+08:00)';
                default: return 'UTC';
            }
        },
        titleData() {
            return this.data.map(item => ({
                ...item,
                durationMin: item.duration ? this.formatDuration(item.duration) : '',
                min: item.duration ? Math.ceil(item.duration / 60) : '',
                on_air_date: item.on_air_date ? this.convertToTimezone(item.on_air_date) : '',
                english_ingested_date: item.english_ingested_date ? this.formatIngestedDates(item.english_ingested_date) : '',
                portuguese_ingested_date: item.portuguese_ingested_date ? this.formatIngestedDates(item.portuguese_ingested_date) : '',
                portuguese_qc_date: item.portuguese_qc_date ? this.formatIngestedDates(item.portuguese_qc_date) : '',
                spanish_ingested_date: item.spanish_ingested_date ? this.formatIngestedDates(item.spanish_ingested_date) : '',
                spanish_qc_date: item.spanish_qc_date ? this.formatIngestedDates(item.spanish_qc_date) : '',
                vietnamese_ingested_date: item.vietnamese_ingested_date ? this.formatIngestedDates(item.vietnamese_ingested_date) : '',
                chinese_ingested_date: item.chinese_ingested_date ? this.formatIngestedDates(item.chinese_ingested_date) : '',
                chinese_qc_date: item.chinese_qc_date ? this.formatIngestedDates(item.chinese_qc_date) : '',
            }))
            .reverse(); // 최신 데이터가 먼저 표시되도록 역순으로 정렬
        },
        filteredTitleData() {
            let data = this.titleData;

            if (this.auth === 'PORTUGUESE') {
                data = data.filter(item => item.portuguese_ingested_date || item.portuguese_qc_date);
            }

            if (this.auth === 'SPANISH') {
                data = data.filter(item => item.spanish_ingested_date || item.spanish_qc_date);
            }

            if (this.auth === 'VIETNAMESE') {
                data = data.filter(item => item.vietnamese_ingested_date);
            }

            if (this.auth === 'CHINESE') {
                data = data.filter(item => item.chinese_ingested_date || item.chinese_qc_date);
            }
            

            // 날짜 필터가 선택된 경우 필터링
            if (this.selectedFilter.includes('ingested_date') || this.selectedFilter === 'on_air_date') {
                return this.filterByDateRange(data);
            } 
            // CP 또는 Category 필터가 선택된 경우 필터링
            else if (this.selectedFilter === 'cp' || this.selectedFilter === 'category') {
                return this.filterByDropdown(data);
            } 
            // 텍스트 검색 필터가 없는 경우 모든 데이터 반환
            else if (this.search.trim() === '') {
                return data;
            }
            
            // 텍스트 검색 필터링
            return data.filter(item => {
                const lowerCaseSearch = this.search.toLowerCase();
                return item[this.selectedFilter]?.toLowerCase().includes(lowerCaseSearch) || false;
            });
        },

    },
    methods: {
        updateTab(index) {
            this.selectedTabIndex = index;
            this.selectedTab = this.filteredTabs[index].value; 
            this.selectedFilter = this.filterOptions[0]?.value || ''; // 기본 필터 설정

            this.updateFilteredData();
        },
        updateFilteredData() {
            if (this.selectedTab === 'Old') {
                this.data = this.allData.filter(item => item.id < 103086426); // Old 탭 → id 103086426 미만
            } else {
                this.data = this.allData.filter(item => item.id >= 103086426); // 나머지 탭 → id 103086426 이상
            }
        },
        
        updatePage(newPage) {
            this.page = newPage;
        },
        formatIngestedDates(jsonString) {
            if (!jsonString) return ''; // jsonString이 유효하지 않으면 빈 문자열 반환
            const dates = JSON.parse(jsonString);
            const timeOffset = this.getTimeOffset(); // auth에 따른 시간 차이 가져오기

            return Object.values(dates)
                .map(date => this.addHours(date, timeOffset))
                .join('\n');
        },

        addHours(dateString, hours) {
            // dateString이 'YYYY-MM-DD HH:mm:ss' 형식일 경우, 이를 'YYYY-MM-DDTHH:mm:ssZ'로 변환
            const utcDateStr = dateString.replace(' ', 'T') + 'Z';
            const utcDate = new Date(utcDateStr);
            
            // 유효하지 않은 날짜는 그대로 반환
            if (isNaN(utcDate.getTime())) {
                return dateString;
            }

            // 지정된 시간만큼 변환
            const adjustedDate = new Date(utcDate.getTime() + (hours * 60 * 60 * 1000));

            // 변환된 시간을 'YYYY-MM-DD HH:mm:ss' 형식으로 반환
            return adjustedDate.toISOString().replace('T', ' ').substring(0, 19);
        },

        getTimeOffset() {
            // 각 auth별로 시간 오프셋 반환
            switch (this.auth) {
                case 'PORTUGUESE': return -3; // BRT (UTC−03:00)
                case 'SPANISH': return -6; // CST (UTC−06:00)
                case 'VIETNAMESE': return +7; // ICT (UTC+07:00)
                case 'CHINESE': return +8; // CST (UTC+08:00)
                default: return 0; // 기본적으로 UTC (0)
            }
        },

        formatDuration(seconds) {
            const totalMinutes = Math.floor(seconds / 60);
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            const remainingSeconds = seconds % 60;
            return `${hours}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
        },
        convertToTimezone(kstDateString) {

            // on_air_date는 KST로 이미 입력되어 있으므로 직접 UTC로 변환 후 타임존 적용
            const localDate = new Date(kstDateString);
            if (isNaN(localDate.getTime())) return kstDateString; // 잘못된 날짜일 경우 원래 문자열 반환

            const utcDate = new Date(localDate.getTime() - 9 * 60 * 60 * 1000); // UTC로 변환
            const adjustedDate = new Date(utcDate.getTime() + this.getTimeOffset() * 60 * 60 * 1000); // 선택된 타임존으로 변환

            // 변환된 시간을 'YYYY-MM-DD HH:mm:ss' 형식으로 반환
            return `${adjustedDate.getFullYear()}-${String(adjustedDate.getMonth() + 1).padStart(2, '0')}-${String(adjustedDate.getDate()).padStart(2, '0')} ` +
                   `${String(adjustedDate.getHours()).padStart(2, '0')}:${String(adjustedDate.getMinutes()).padStart(2, '0')}:${String(adjustedDate.getSeconds()).padStart(2, '0')}`;
        },

        updateDateRange() {
            if (this.selectedDates.length === 2) {
                const [fromDate, toDate] = this.selectedDates;
                this.dateRangeText = `${fromDate} - ${toDate}`;
            }
        },
        filterByDateRange(data) {
            if (!this.selectedDates.length || this.selectedDates.length < 2) return data;
            const [fromDate, toDate] = this.selectedDates;
            return data.filter(item => {
                const itemDate = new Date(item[this.selectedFilter].split(' ')[0]);
                return itemDate >= new Date(fromDate) && itemDate <= new Date(toDate);
            });
        },
        filterByDropdown(data) {
            if (!this.search) return data;
            return data.filter(item => item[this.selectedFilter] === this.search);
        },
        setDropdownOptions() {
            const field = this.selectedFilter;
            this.dropdownOptions = [...new Set(this.data.map(item => item[field]))];
        },
        // Excel로 데이터 내보내기
        exportToExcel() {
            // filteredHeaders가 undefined일 경우 경고 출력 및 함수 종료
            if (!Array.isArray(this.filteredHeaders) || this.filteredHeaders.length === 0) {
                console.error("Headers are not defined or empty. Please ensure headers are loaded before exporting.");
                return;
            }

            // 선택된 헤더 순서에 따라 데이터 정렬
            const orderedData = this.filteredTitleData.map(item => {
                return this.filteredHeaders.reduce((acc, header) => {
                    acc[header.text] = item[header.value] !== undefined ? item[header.value] : ''; // 값이 없으면 빈 문자열로 설정
                    return acc;
                }, {});
            });

            const ws = XLSX.utils.json_to_sheet(orderedData, { header: this.filteredHeaders.map(h => h.text) }); // 필터링된 데이터를 시트로 변환
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Subtitle Data');
            XLSX.writeFile(wb, 'subtitle_data.xlsx'); // 파일 저장
        }

    },
    mounted() {
        axios.get('/api/data') // Fetch data from API
            .then(response => {
                this.allData = response.data; // 원본 데이터 저장
                this.data = this.allData.filter(item => item.id >= 103086426); // Master 기본 필터링
                this.loading = false;
                this.updateFilteredData(); // 초기 데이터를 로드한 후 필터링
            })
            .catch(error => {
                console.error('Error fetching data:', error);
                this.loading = false;
            });
    }
};
</script>

<style scoped>

/* Table 전체 가로 스크롤 설정 */
.scrollable-table {
    overflow-x: auto;
    width: 100%;
}

/* Vuetify에서 고정 열 스타일 */
.custom-table .v-data-table__wrapper {
    overflow-x: auto !important;
}

.custom-table td,
.custom-table th {
    white-space: nowrap; /* 텍스트 줄바꿈 방지 */
}

::v-deep .v-data-table td,
::v-deep .v-data-table th {
    padding: 0; /* 기본 padding 제거 */
    white-space: nowrap; /* 텍스트 줄바꿈 방지 */
}



.v-data-table-header,
::v-deep .v-data-table-header th {
    position: sticky;
    background-color: #ededed !important;
    color: rgb(99, 99, 99) !important;
    text-align: center  !important; /* 좌우 중간 정렬 */
    vertical-align: middle !important; /* 상하 중간 정렬 */
    white-space: nowrap !important; /* 텍스트 줄바꿈 방지 */
    padding: 10px;
    border-left: 1px solid #ededed; /* 헤더 사이의 빈틈 제거 */
}

::v-deep .v-data-table__wrapper {
    overflow-x: auto !important; /* Always show horizontal scroll if needed */
    margin-top: 45px;
    min-width: 800px; /* Adjust this to a value that fits your design */
}

::v-deep .v-data-table__wrapper td {
    white-space: pre-line !important; /* Prevent text wrapping */
}

::v-deep .v-data-table-header th:hover {
  color: black !important;
}

::v-deep .v-data-table tbody tr:hover {
  background-color: #e5eef9 !important;
}

.filter-select {
    margin: 30px 20px 30px 10px;
}

::v-deep .v-toolbar__content {
    margin-top: 30px !important;
    margin-bottom: 15px;
}
</style>
